import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Coins, Users, Zap, Plus, History, Gift, Tag, Box, Ticket, CheckCircle2, ArrowUpCircle } from 'lucide-react';
import { collection, addDoc, serverTimestamp, query, limit, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

const ENTRY_FEE = 10;
const FAKE_USERS = [
  '@hypebeast99', '@sneaker_head_za', '@drip_lord', '@jozi_kicks', 
  '@archive_god', '@y2k_stepper', '@sole_collector', '@high_fashion_cpt', 
  '@vintage_plug', '@grail_hunter', '@dune_boy', '@streetwear_king'
];

const GameStyles = () => (
  <style>{`
    .scrollbar-hide::-webkit-scrollbar { display: none; }
    .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }

    @keyframes starScroll {
      from { transform: translateY(0); }
      to { transform: translateY(100vh); }
    }

    @keyframes giantPulse {
      0% { transform: scale(0.5); opacity: 0; filter: blur(20px); }
      20% { transform: scale(1.1); opacity: 1; filter: blur(0px); }
      80% { transform: scale(1); opacity: 1; filter: blur(0px); }
      100% { transform: scale(2); opacity: 0; filter: blur(40px); }
    }

    @keyframes shatter {
      0% { transform: scale(1) rotate(0); filter: brightness(1) blur(0); }
      100% { transform: scale(1.5) rotate(20deg); filter: brightness(5) blur(30px); opacity: 0; }
    }

    @keyframes shake {
      0% { transform: translate(1px, 1px) rotate(0deg); }
      25% { transform: translate(-3px, -2px) rotate(-1deg); }
      50% { transform: translate(3px, 0px) rotate(1deg); }
      75% { transform: translate(-2px, 3px) rotate(0deg); }
      100% { transform: translate(1px, -1px) rotate(0deg); }
    }

    @keyframes plasmaPulse {
      0%, 100% { opacity: 0.8; transform: scaleY(1); }
      50% { opacity: 1; transform: scaleY(1.3); }
    }

    .screen-shake { animation: shake 0.1s infinite; }
    .heavy-shake { animation: shake 0.05s infinite; }

    .crash-flash {
      position: absolute; inset: 0; background: #fff; z-index: 1000;
      animation: flash 0.8s forwards; pointer-events: none;
    }
    @keyframes flash { 0% { opacity: 1; } 100% { opacity: 0; } }
  `}</style>
);

export const HypeCrashModal = ({ isOpen, onClose, profile, updateProfile, onBuyCredits }: any) => {
    const [gameState, setGameState] = useState<'idle' | 'waiting' | 'flying' | 'crashed'>('idle');
    const [playerState, setPlayerState] = useState<'idle' | 'bet_placed' | 'cashed_out'>('idle');
    
    const [currentMultiplier, setCurrentMultiplier] = useState<number>(1.00);
    const [localBalance, setLocalBalance] = useState<number>(0); 
    const [countdown, setCountdown] = useState<number>(5.0);
    const [winAmount, setWinAmount] = useState<number>(0);
    const [wonLoot, setWonLoot] = useState<any>(null);
    const [livePlayers, setLivePlayers] = useState<any[]>([]);
    
    const [milestones, setMilestones] = useState<any[]>([]);
    const [rewardPool, setRewardPool] = useState<any[]>([]);
    const [feedHistory, setFeedHistory] = useState<any[]>([]);

    const requestRef = useRef<number>();
    const engineRef = useRef({
        startTime: 0,
        targetCrash: 0,
        currentMult: 1.00,
        isGameOver: false
    });

    useEffect(() => {
        if (profile?.cred !== undefined) setLocalBalance(profile.cred);
    }, [profile?.cred]);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const q = query(collection(db, 'products'), limit(15));
                const snapshot = await getDocs(q);
                
                let products = snapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        type: 'product',
                        image: data.image || data.images?.[0],
                        name: data.name,
                        brand: data.brand
                    };
                });

                const mixedPool = [
                    ...products,
                    { type: 'voucher', name: '30% OFF VOUCHER' },
                    { type: 'voucher', name: 'FREE SHIPPING PASS' },
                    { type: 'credits', name: '1000 CRD SECURED', val: 1000 }
                ];
                setRewardPool(mixedPool);

                const dynamicMilestones = [
                    { mult: 2.0, label: 'FREE SHIPPING', type: 'shipping', value: 'FREESHIP', color: '#ffffff', icon: Gift },
                    { mult: 5.0, label: '15% OFF VOUCHER', type: 'discount', value: 'HYPE15', color: '#3b82f6', icon: Ticket },
                    { mult: 10.0, label: '30% OFF VOUCHER', type: 'discount', value: 'HYPE30', color: '#a855f7', icon: Tag },
                    { mult: 25.0, label: products[0]?.name || 'FREE SNEAKER', type: 'product', value: 'SNEAKER_WIN', color: '#39FF14', icon: Box, image: products[0]?.image },
                    { mult: 50.0, label: products[1]?.name || 'MYSTERY GEAR', type: 'product', value: 'MYSTERY_BOX', color: '#eab308', icon: Zap, image: products[1]?.image },
                ];
                setMilestones(dynamicMilestones);

                const history = Array.from({ length: 6 }).map(() => ({
                    user: FAKE_USERS[Math.floor(Math.random() * FAKE_USERS.length)],
                    mult: (Math.random() * 20 + 2).toFixed(2),
                    loot: mixedPool[Math.floor(Math.random() * mixedPool.length)]
                })).sort((a, b) => Number(b.mult) - Number(a.mult));
                setFeedHistory(history);

            } catch (error) {
                console.error("Failed to load products for Crash:", error);
            }
        };

        fetchProducts();
        if (isOpen && gameState === 'idle') startLobby();
        
        return () => {
            if (requestRef.current) cancelAnimationFrame(requestRef.current);
        };
    }, [isOpen]);

    const generateCrashPoint = () => {
        const roll = Math.random() * 100;
        if (roll < 5) return 1.00; 
        if (roll < 45) return parseFloat((Math.random() * (1.99 - 1.01) + 1.01).toFixed(2));
        if (roll < 75) return parseFloat((Math.random() * (4.99 - 2.00) + 2.00).toFixed(2));
        if (roll < 90) return parseFloat((Math.random() * (9.99 - 5.00) + 5.00).toFixed(2));
        if (roll < 98) return parseFloat((Math.random() * (24.99 - 10.00) + 10.00).toFixed(2));
        return parseFloat((Math.random() * (100.00 - 25.00) + 25.00).toFixed(2));
    };

    const generateFakePlayers = (targetCrash: number) => {
        const players = [];
        const numPlayers = Math.floor(Math.random() * 8) + 6;
        for (let i = 0; i < numPlayers; i++) {
            const name = FAKE_USERS[Math.floor(Math.random() * FAKE_USERS.length)];
            const bet = Math.floor(Math.random() * 100) + 10;
            const targetMult = (Math.random() * (targetCrash + 3 - 1.1) + 1.1).toFixed(2);
            players.push({ id: i, name, bet, targetMult: Math.max(1.1, Number(targetMult)), cashedOut: false, win: 0 });
        }
        return players;
    };

    const startLobby = () => {
        setGameState('waiting');
        setPlayerState('idle'); 
        setCurrentMultiplier(1.00);
        setWinAmount(0);
        setWonLoot(null);
        setLivePlayers([]);

        const targetCrash = generateCrashPoint();
        engineRef.current.targetCrash = targetCrash;
        const upcomingBots = generateFakePlayers(targetCrash);

        let timeLeft = 5.0; 
        const interval = setInterval(() => {
            timeLeft -= 0.1;
            setCountdown(Math.max(0, parseFloat(timeLeft.toFixed(1))));
            
            if (upcomingBots.length > 0 && Math.random() > 0.3) {
                const bot = upcomingBots.pop();
                if (bot) setLivePlayers(prev => [bot, ...prev]);
            }

            if (timeLeft <= 0) {
                clearInterval(interval);
                takeOff();
            }
        }, 100);
    };

    const placeBet = () => {
        if (localBalance < ENTRY_FEE) return;
        const newBalance = localBalance - ENTRY_FEE;
        setLocalBalance(newBalance);
        if (updateProfile) updateProfile({ cred: newBalance });
        setPlayerState('bet_placed');
        setLivePlayers(prev => [{ id: 999, name: 'YOU', bet: ENTRY_FEE, targetMult: 9999, cashedOut: false, win: 0, isMe: true } as any, ...prev]);
    };

    const takeOff = () => {
        setGameState('flying');
        engineRef.current.startTime = Date.now();
        engineRef.current.currentMult = 1.00;
        engineRef.current.isGameOver = false;
        requestRef.current = requestAnimationFrame(gameLoop);
    };

    const gameLoop = useCallback(() => {
        const engine = engineRef.current;
        if (engine.isGameOver) return; 

        const timeElapsed = (Date.now() - engine.startTime) / 1000;
        const newMult = 1.00 + (timeElapsed * 0.08) + Math.pow(1.05, timeElapsed) - 1;
        
        if (newMult >= engine.targetCrash) { 
            engine.currentMult = engine.targetCrash;
            triggerCrash();
            return;
        }

        engine.currentMult = newMult;
        setCurrentMultiplier(newMult);

        setLivePlayers(prev => {
            let updated = false;
            const newPlayers = prev.map(p => {
                if (!p.cashedOut && !p.isMe && newMult >= p.targetMult) {
                    updated = true;
                    const randomLoot = rewardPool[Math.floor(Math.random() * rewardPool.length)];
                    setFeedHistory(history => [{ user: p.name, mult: p.targetMult.toFixed(2), loot: randomLoot, isMe: false }, ...history].slice(0, 15));
                    return { ...p, cashedOut: true, win: Math.floor(p.bet * p.targetMult) };
                }
                return p;
            });
            return updated ? newPlayers : prev;
        });

        requestRef.current = requestAnimationFrame(gameLoop);
    }, [rewardPool]);

    const cashOut = async () => {
        if (gameState !== 'flying' || playerState !== 'bet_placed' || !profile?.uid) return;
        
        const lockedMultiplier = parseFloat(engineRef.current.currentMult.toFixed(2));
        const profit = Math.floor(ENTRY_FEE * lockedMultiplier);
        const unlockedLoot = milestones.slice().reverse().find(m => lockedMultiplier >= m.mult);
        
        setPlayerState('cashed_out');
        setWinAmount(profit);
        setWonLoot(unlockedLoot); 
        
        const updatedBalance = localBalance + profit;
        setLocalBalance(updatedBalance);
        
        setLivePlayers(prev => prev.map(p => p.isMe ? { ...p, cashedOut: true, win: profit } : p));
        setFeedHistory(prev => [{ user: 'YOU', mult: lockedMultiplier.toFixed(2), loot: { type: 'credits', name: `+${profit} CRD` }, isMe: true }, ...prev].slice(0, 15));

        try { 
            if (updateProfile) await updateProfile({ cred: updatedBalance }); 

            if (unlockedLoot) {
                await addDoc(collection(db, "rewards"), {
                    userId: profile.uid,
                    title: unlockedLoot.label,
                    rewardType: unlockedLoot.type, 
                    value: unlockedLoot.value,
                    sourceGame: 'Hype Crash',
                    multiplier: lockedMultiplier,
                    status: 'available',
                    createdAt: serverTimestamp()
                });

                await addDoc(collection(db, "notifications"), {
                    userId: profile.uid,
                    type: 'reward',
                    title: 'Hype Crash Bag Secured! 🏆',
                    message: `You cashed out at ${lockedMultiplier.toFixed(2)}x and secured ${unlockedLoot.label}. Claim it in your Vault!`,
                    read: false,
                    createdAt: serverTimestamp()
                });
            }
        } catch(e) { 
            console.error("Fulfillment failed:", e); 
        }
    };

    const triggerCrash = () => {
        engineRef.current.isGameOver = true;
        setGameState('crashed');
        setTimeout(() => startLobby(), 3500); 
    };

    const getRocketAnimation = () => {
        if (gameState === 'crashed') return 'shatter 1s forwards';
        if (gameState === 'flying') return 'shake 0.1s infinite';
        return 'none';
    };

    const getMultiplierColor = () => {
        if (gameState === 'crashed') return 'text-red-500';
        if (playerState === 'cashed_out') return 'text-emerald-400';
        return 'text-cyan-400';
    };

    const starSpeed = gameState === 'flying' ? `${5 / Math.max(1, currentMultiplier * 0.2)}s` : '20s';

    if (!isOpen) return null;

    return (
        <div className="w-full h-full min-h-screen bg-black flex flex-col font-sans overflow-y-auto overflow-x-hidden lg:overflow-hidden text-white rounded-none fixed inset-0 z-[999]">
            <GameStyles />
            <div className="flex flex-col lg:flex-row min-h-full rounded-none">
                
                {/* 🟢 LEFT PANEL: Live Feed & Lobby (Moves under on Mobile with extra bottom padding to clear sticky footer) */}
                <div className="flex w-full lg:w-80 order-2 lg:order-1 bg-black border-t lg:border-t-0 lg:border-r border-zinc-800/50 flex-col relative z-20 shadow-2xl rounded-none shrink-0 min-h-[400px] lg:min-h-0 pb-24 lg:pb-0">
                    <div className="p-4 border-b border-zinc-800/50 flex items-center justify-between bg-gradient-to-r from-zinc-950/20 to-black rounded-none shrink-0">
                        <div className="flex items-center gap-2">
                            <Users size={16} className="text-cyan-400"/>
                            <span className="text-xs font-black uppercase tracking-widest text-white">Live Lobby</span>
                        </div>
                        {gameState === 'waiting' && <span className="text-[10px] bg-gradient-to-r from-cyan-950/40 to-black border border-cyan-800/50 text-cyan-400 px-2 py-0.5 rounded-none animate-pulse">Boarding...</span>}
                    </div>
                    
                    <div className="flex justify-between px-4 py-2 bg-gradient-to-r from-zinc-950/40 to-black text-[9px] font-black text-zinc-500 uppercase tracking-widest border-b border-zinc-800/20 rounded-none shrink-0">
                        <span>User</span><span>Bet</span><span>Win</span>
                    </div>

                    <div className="flex-1 lg:flex-none lg:h-1/2 overflow-y-auto p-2 space-y-1 scrollbar-hide bg-black rounded-none min-h-[150px]">
                        {livePlayers.map((p, i) => (
                            <div key={i} className={`flex items-center justify-between p-2 rounded-none transition-all duration-300 border ${p.isMe ? 'bg-gradient-to-r from-cyan-950/20 to-black border-cyan-800/30' : p.cashedOut ? 'bg-gradient-to-r from-emerald-950/20 to-black border-emerald-800/30' : 'bg-transparent border-transparent'}`}>
                                <div className="w-1/3 truncate text-[10px] font-bold text-zinc-300">{p.name}</div>
                                <div className="w-1/3 text-center text-[10px] font-mono text-zinc-500">{p.bet}</div>
                                <div className="w-1/3 text-right">
                                    {p.cashedOut ? (
                                        <div className="flex flex-col">
                                            <span className="text-[9px] text-emerald-400">{p.targetMult.toFixed(2)}x</span>
                                            <span className="text-xs font-black text-emerald-500">+{p.win}</span>
                                        </div>
                                    ) : (
                                        <span className="text-[10px] text-cyan-600 animate-pulse uppercase">Flying</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="h-72 lg:flex-1 border-t border-zinc-800/50 flex flex-col bg-black rounded-none shrink-0">
                        <div className="px-4 py-3 flex items-center gap-2 border-b border-zinc-800/50 rounded-none bg-gradient-to-r from-zinc-950/40 to-black shrink-0">
                            <History size={12} className="text-cyan-400"/>
                            <span className="text-[9px] font-black uppercase tracking-widest text-zinc-400">Live Drops</span>
                        </div>
                        <div className="flex-1 overflow-y-auto p-2 space-y-2 scrollbar-hide rounded-none">
                            {feedHistory.map((item, i) => (
                                <div key={i} className={`flex items-center gap-3 px-2 py-2 rounded-none border ${item.isMe ? 'bg-gradient-to-r from-cyan-950/20 to-black border-cyan-800/30' : 'bg-gradient-to-r from-zinc-950/20 to-black border-zinc-800/30'}`}>
                                    <div className="w-8 h-8 rounded-none bg-black overflow-hidden flex items-center justify-center shrink-0 p-0.5 border border-zinc-700/50">
                                        {item.loot?.image ? <img src={item.loot.image} alt="loot" className="w-full h-full object-cover rounded-none" /> :
                                         item.loot?.type === 'voucher' ? <Tag className="text-indigo-400" size={14} /> :
                                         <Coins className="text-amber-400" size={14} />}
                                    </div>
                                    <div className="flex flex-col flex-1 min-w-0">
                                        <span className={`text-[8px] font-bold truncate uppercase ${item.isMe ? 'text-cyan-400' : 'text-zinc-500'}`}>{item.user} secured</span>
                                        <span className="text-[10px] font-black truncate text-white">{item.loot?.name || 'LOOT'}</span>
                                    </div>
                                    <span className="text-[10px] text-amber-400 font-mono pl-2">{item.mult}x</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* 🟢 RIGHT PANEL: Main Game Area (Moves top on Mobile) */}
                <div className={`w-full lg:flex-1 h-[70vh] lg:h-auto order-1 lg:order-2 shrink-0 lg:shrink flex flex-col relative bg-black rounded-none ${gameState === 'crashed' ? 'heavy-shake' : (gameState === 'flying' && currentMultiplier > 3 ? 'screen-shake' : '')}`}>
                    
                    {gameState === 'crashed' && <div className="crash-flash" />}

                    {/* Header */}
                    <div className="absolute top-0 left-0 w-full p-4 lg:p-6 flex justify-between items-start z-50 pointer-events-none rounded-none">
                        <div className="flex items-center gap-2 drop-shadow-md bg-gradient-to-r from-cyan-950/20 to-black px-3 py-2 rounded-none border border-cyan-800/30">
                            <Zap className="text-cyan-400 animate-pulse" size={16} />
                            <h2 className="text-xs lg:text-sm font-black uppercase italic tracking-tighter text-cyan-400">HYPE CRASH</h2>
                        </div>
                        <div className="flex items-center gap-2 lg:gap-3 pointer-events-auto">
                            <div className="bg-gradient-to-r from-amber-950/20 to-black border border-amber-800/30 rounded-none px-3 py-2 lg:px-4 lg:py-3 flex items-center gap-2 shadow-[0_0_20px_rgba(251,191,36,0.1)]">
                                <Coins size={14} className="text-amber-400"/>
                                <span className="font-mono text-amber-400 font-black text-xs lg:text-sm">{localBalance}</span>
                            </div>
                            <button onClick={onClose} className="p-2 lg:p-3 bg-gradient-to-r from-zinc-950/40 to-black border border-zinc-800/50 rounded-none text-zinc-400 hover:text-white hover:border-red-500/50 transition-all">
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Flight Visuals Area */}
                    <div className="flex-1 relative overflow-hidden bg-black rounded-none flex flex-col justify-center">
                        
                        {/* Background Visuals */}
                        <div className="absolute inset-0 pointer-events-none overflow-hidden z-0 rounded-none">
                            <div className="absolute inset-0 opacity-[0.05] bg-[linear-gradient(rgba(255,255,255,1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,1)_1px,transparent_1px)] bg-[size:60px_60px] rounded-none" style={{ transform: `translateY(${(currentMultiplier * 60) % 60}px)` }} />
                            <div className="absolute inset-0 opacity-20 bg-[radial-gradient(white_1px,transparent_1px)] bg-[size:100px_100px] rounded-none" style={{ animation: `starScroll ${starSpeed} linear infinite` }} />
                        </div>

                        {/* Countdown Overlay */}
                        {gameState === 'waiting' && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center z-40 bg-black/60 backdrop-blur-sm rounded-none">
                                <div key={Math.ceil(countdown)} className="text-[8rem] lg:text-[15rem] font-black italic text-cyan-400 animate-[giantPulse_1s_ease-out_forwards]">
                                    {Math.ceil(countdown)}
                                </div>
                            </div>
                        )}

                        {/* Secured Loot Overlay */}
                        {playerState === 'cashed_out' && (
                            <div className="absolute top-[15%] lg:top-[20%] left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-3 w-full px-4 rounded-none">
                                <div className="bg-gradient-to-r from-emerald-950/40 to-black border border-emerald-800/50 text-emerald-400 px-4 lg:px-8 py-2 lg:py-4 rounded-none font-black uppercase tracking-[0.2em] text-[10px] lg:text-lg shadow-[0_0_50px_rgba(16,185,129,0.2)] flex items-center gap-2 lg:gap-3">
                                    <CheckCircle2 size={20}/> SECURED +{winAmount} CRD
                                </div>
                                {wonLoot && (
                                    <div className="bg-gradient-to-r from-zinc-950/40 to-black text-white px-3 lg:px-8 py-2 lg:py-4 rounded-none border border-zinc-800/50 flex items-center gap-2 lg:gap-3 text-center">
                                        {wonLoot.image ? (
                                            <img src={wonLoot.image} alt="loot" className="w-6 h-6 lg:w-10 lg:h-10 object-cover rounded-none" />
                                        ) : (
                                            <wonLoot.icon size={16} color={wonLoot.color} className="shrink-0" /> 
                                        )}
                                        <span className="font-black uppercase tracking-widest text-[8px] lg:text-sm" style={{ color: wonLoot.color }}>UNLOCKED: {wonLoot.label}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* 🟢 LOOT TRACK (Side Track) */}
                        <div className="absolute top-0 bottom-0 right-2 lg:right-16 w-32 lg:w-56 flex flex-col justify-center gap-6 z-10 pointer-events-none rounded-none">
                            <div 
                                className="relative transition-transform duration-100 ease-linear rounded-none h-full" 
                                style={{ transform: `translateY(${Math.log(currentMultiplier) * 650}px)` }}
                            >
                                {milestones.map((ms, idx) => {
                                    const isPassed = currentMultiplier >= ms.mult;
                                    const yPos = Math.log(ms.mult) * 650;
                                    
                                    return (
                                        <div 
                                            key={idx} 
                                            className={`absolute right-0 flex items-center justify-end gap-2 lg:gap-4 transition-all duration-300 rounded-none w-full ${isPassed ? 'opacity-100' : 'opacity-40 scale-90'}`}
                                            style={{ bottom: `calc(35% + ${yPos}px)`, transform: 'translateY(100%)' }}
                                        >
                                            <div className={`flex flex-col text-right px-2 lg:px-4 py-1 lg:py-2 border rounded-none ${isPassed ? 'bg-gradient-to-r from-emerald-950/20 to-black border-emerald-800/30' : 'bg-gradient-to-r from-zinc-950/20 to-black border-zinc-800/30'}`}>
                                                <span className={`text-[8px] lg:text-[10px] font-black tracking-widest ${isPassed ? 'text-emerald-400' : 'text-zinc-500'}`}>{ms.mult.toFixed(1)}x</span>
                                                <span className="text-[8px] lg:text-xs font-bold text-white uppercase truncate max-w-[70px] lg:max-w-[140px]">{ms.label}</span>
                                            </div>
                                            <div className={`w-6 h-6 lg:w-14 lg:h-14 flex items-center justify-center shrink-0 border lg:border-2 rounded-none bg-black ${isPassed ? 'border-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.3)]' : 'border-zinc-800'}`}>
                                                {ms.image ? (
                                                    <img src={ms.image} alt="loot" className={`w-full h-full object-cover rounded-none ${!isPassed && 'grayscale opacity-50'}`} />
                                                ) : (
                                                    <ms.icon size={12} className={isPassed ? 'text-emerald-400' : 'text-zinc-600 lg:w-4 lg:h-4'} />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* 🟢 THE ROCKET & MULTIPLIER */}
                        <div className="absolute bottom-[20%] lg:bottom-[25%] left-1/2 -translate-x-1/2 z-20 flex flex-col items-center rounded-none w-[180px] lg:w-[250px]">
                            
                            {/* Live Multiplier anchored in front of Rocket */}
                            {(gameState === 'flying' || gameState === 'crashed') && (
                                <div className="mb-2 lg:mb-4 flex flex-col items-center pointer-events-none rounded-none">
                                    <div className={`text-4xl lg:text-5xl font-black tabular-nums transition-colors duration-100 ${getMultiplierColor()} drop-shadow-[0_0_20px_currentColor]`}>
                                        {currentMultiplier.toFixed(2)}x
                                    </div>
                                </div>
                            )}

                            <div style={{ animation: getRocketAnimation() }} className="relative flex justify-center overflow-visible h-40 md:h-64 rounded-none">
                                {/* Flame Glow */}
                                {gameState === 'flying' && (
                                    <div className="absolute top-[40%] left-1/2 -translate-x-1/2 w-24 lg:w-32 h-[250px] lg:h-[350px] bg-cyan-400/10 blur-[60px] lg:blur-[80px] rounded-none animate-pulse" />
                                )}
                                
                                <svg width="220" height="480" viewBox="0 0 353 800" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full drop-shadow-[0_0_20px_rgba(34,211,238,0.2)] lg:drop-shadow-[0_0_30px_rgba(34,211,238,0.2)] overflow-visible">
                                    <path d="M42.5344 526.469C37.6792 494.122 32.8708 462.21 28.261 430.269C27.6009 425.696 27.8496 420.984 27.7684 416.335C27.6577 409.991 30.8258 405.317 35.46 401.359C46.2197 392.168 56.8746 382.854 67.7052 373.748C70.8165 371.132 71.5772 368.284 71.6241 364.222C71.858 343.932 71.747 323.579 73.3592 303.376C76.3798 265.526 82.696 228.101 93.1014 191.562C99.0474 170.682 106.726 150.291 113.809 129.743C116.189 122.839 116.825 122.722 123.52 125.321C149.798 135.52 176.602 136.993 203.844 129.978C208.97 128.658 213.968 126.723 218.871 124.699C222.782 123.084 224.855 123.823 226.477 127.936C240.274 162.939 251.043 198.822 257.998 235.807C265.742 276.986 270.194 318.474 268.805 360.447C268.557 367.928 271.525 373.333 277.357 377.96C287.002 385.612 296.32 393.69 305.582 401.81C311.819 407.28 313.937 414.452 312.739 422.588C305.759 469.989 298.776 517.39 291.673 564.773C291.394 566.633 289.883 568.308 288.945 570.069C287.345 568.865 285.347 567.954 284.208 566.41C276.406 555.83 268.977 544.968 260.953 534.563C259.196 532.284 255.666 530.666 252.713 530.168C246.953 529.197 245.448 527.907 246.372 522.016C248.333 509.529 250.263 497.034 252.467 484.589C253.181 480.558 252.458 478.733 248.039 479.672C246.917 479.91 245.716 479.831 244.553 479.806C235.208 479.608 233.414 477.312 235.374 468.149C243.996 427.853 247.059 387.163 243.978 346.043C243.926 345.353 243.639 344.68 242.541 344.02C242.363 345.013 242.118 346 242.016 347.001C239.224 374.469 237.333 402.066 233.409 429.371C229.679 455.33 223.757 480.971 218.961 506.782C217.91 512.442 215.234 515.76 209.525 517.573C182.975 526.007 156.603 526.319 130.179 517.118C125.493 515.486 122.669 512.953 121.778 507.82C116.293 476.221 110.132 444.73 105.177 413.05C102.198 394.003 101.011 374.676 98.989 355.478C98.5667 351.469 98.0138 347.473 97.5211 343.471C96.9976 343.485 96.4741 343.498 95.9507 343.512C96.4624 367.594 96.059 391.742 97.7846 415.737C99.0906 433.898 103.05 451.874 105.959 469.911C107.111 477.057 105.039 479.668 97.5313 479.836C87.4126 480.062 87.3735 480.068 88.9036 490.312C90.4757 500.838 92.1056 511.355 93.7659 521.868C94.8289 528.598 94.089 529.724 87.5177 529.785C83.2623 529.825 80.4001 531.673 78.0389 535.114C70.7926 545.673 63.4414 556.163 55.9336 566.536C54.838 568.05 52.6655 568.785 50.9914 569.88C50.1009 568.228 48.6872 566.66 48.4037 564.909C46.3558 552.258 44.5365 539.571 42.5344 526.469ZM120.729 184.251C112.689 197.884 110.235 212.429 113.529 227.938C118.851 252.992 143.201 273.185 172.079 272.417C208.328 271.453 235.694 238.139 227.493 202.957C221.856 178.773 206.732 163.389 182.561 158.177C157.627 152.801 134.967 162.733 120.729 184.251Z" fill="#e2e8f0"/>
                                    <g style={{ opacity: gameState === 'flying' ? 1 : 0 }}>
                                        <path style={{ transformOrigin: 'top center', animation: 'plasmaPulse 0.1s infinite' }} d="M226.342 657.702C225.81 656.363 225.429 655.392 225.049 654.42C224.751 654.471 224.453 654.521 224.155 654.572C224.155 662.113 224.094 669.656 224.166 677.197C224.452 707.085 214.408 733.528 197.461 757.698C194.789 761.51 192.348 765.487 189.601 769.242C188.952 770.13 187.5 770.429 186.417 770.999C185.889 769.904 184.847 768.779 184.908 767.717C185.968 749.266 176.578 721.918 170.637 700.82C170.145 701.386 169.599 701.736 169.47 702.202C165.041 718.188 160.308 734.103 156.445 750.226C155.118 755.763 156.048 761.828 155.778 767.644C155.725 768.786 154.891 769.892 154.417 771.015C153.138 770.47 151.349 770.292 150.655 769.327C134.417 746.731 120.184 723.174 118.089 694.483C117.098 680.913 116.833 667.291 116.184 652.438C114.012 659.805 111.861 666.233 110.254 672.794C108.555 679.731 107.44 686.811 105.909 693.792C105.632 695.053 104.575 696.142 103.878 697.31C102.856 696.605 101.484 696.126 100.864 695.162C77.7217 659.206 74.7706 602.928 114.629 567.101C118.554 563.572 123.108 560.686 127.6 557.867C130.748 555.891 132.316 553.561 132.165 549.764C131.941 544.113 132.145 538.446 132.042 532.788C131.969 528.788 133.588 527.265 137.579 528.353C159.584 534.354 181.628 534.295 203.635 528.444C207.279 527.475 208.577 528.609 208.561 532.138C208.539 537.294 208.977 542.481 208.539 547.596C208.105 552.66 209.657 555.918 214.032 558.736C236.429 573.164 250.462 593.334 254.601 619.895C258.666 645.976 253.833 670.494 240.966 693.453C240.177 694.862 238.46 695.751 237.175 696.883C236.365 695.401 235.199 694.008 234.803 692.423C231.945 680.993 229.244 669.525 226.342 657.702ZM162.025 633.671C163.819 630.306 165.429 626.824 167.457 623.606C169.842 619.82 171.454 619.784 173.361 623.73C176.171 629.546 178.711 635.522 180.912 641.594C183.884 649.792 186.425 658.146 189.468 667.397C197.757 648.098 198.442 629.677 190.33 611.351C184.22 597.551 177.467 584.036 170.969 570.41C170.116 569.471 169.722 569.052C163.669 581.932 157.67 594.663 151.7 607.408C145.009 621.691 142.63 636.562 146.073 652.088C147.175 657.055 148.885 661.887 150.627 667.841C154.671 655.741 158.237 645.072 162.025 633.671Z" fill="#22d3ee"/>
                                    </g>
                                    <path d="M189.079 51.9184C198.881 71.1745 208.472 90.13 218.144 109.044C220.165 112.996 218.91 115.131 214.977 116.647C185.266 128.1 155.668 128.363 126.059 116.208C121.806 114.462 120.944 112.049 122.75 108.069C133.333 84.754 144.53 61.7635 158.856 40.4555C167.71 27.2876 172.114 27.2157 181.181 39.9655C183.877 43.7563 186.332 47.7191 189.079 51.9184Z" fill="white"/>
                                    <path d="M171.39 166C198.667 166 220.78 188.112 220.78 215.39C220.78 242.667 198.667 264.78 171.39 264.78C144.112 264.78 122 242.667 122 215.39C122 188.113 144.113 166 171.39 166ZM161.197 190C156.91 190 153.189 190.598 150.036 191.793C146.883 192.988 144.451 194.719 142.74 196.986C141.03 199.233 140.185 201.943 140.206 205.117C140.185 208.745 141.339 211.733 143.668 214.083C145.997 216.412 149.542 218.008 154.303 218.874L162.588 220.451C164.566 220.822 166.02 221.338 166.947 221.997C167.874 222.636 168.348 223.491 168.369 224.562C168.349 225.758 167.73 226.716 166.515 227.438C165.299 228.159 163.722 228.52 161.784 228.52C159.641 228.52 157.879 228.066 156.498 227.159C155.117 226.252 154.261 224.933 153.932 223.202L139 223.573C139.556 228.272 141.783 232.023 145.678 234.826C149.594 237.609 154.942 239 161.723 239C165.968 239 169.74 238.34 173.037 237.021C176.335 235.682 178.932 233.806 180.828 231.395C182.745 228.983 183.713 226.15 183.733 222.894C183.713 219.431 182.538 216.648 180.209 214.546C177.901 212.444 174.377 210.94 169.637 210.033L160.485 208.302C158.487 207.89 157.096 207.343 156.312 206.663C155.55 205.983 155.179 205.179 155.199 204.252C155.179 203.057 155.776 202.108 156.992 201.407C158.229 200.707 159.723 200.357 161.475 200.356C162.814 200.356 163.989 200.583 164.999 201.036C166.03 201.469 166.865 202.077 167.504 202.86C168.143 203.623 168.534 204.499 168.679 205.488L182.559 205.117C182.167 200.418 180.117 196.729 176.407 194.05C172.698 191.35 167.627 190 161.197 190ZM195.796 223.408C191.491 223.408 188 226.899 188 231.204C188 235.509 191.491 238.999 195.796 238.999C200.101 238.999 203.591 235.509 203.591 231.204C203.591 226.899 200.101 223.408 195.796 223.408Z" fill="#0891b2"/>
                                </svg>
                            </div>
                        </div>

                        {/* Flew Away Marker (Pushed up to clear sticky console on mobile) */}
                        {gameState === 'crashed' && (
                            <div className="absolute bottom-20 lg:bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center justify-center w-[90%] max-w-[400px] rounded-none">
                                <div className="bg-gradient-to-r from-red-950/20 to-black text-red-500 font-black uppercase tracking-[0.2em] text-sm lg:text-xl px-4 lg:px-6 py-2 lg:py-4 border border-red-800/30 shadow-[0_0_30px_rgba(239,68,68,0.2)] rounded-none text-center w-full">
                                    Flew Away @ {engineRef.current.targetCrash.toFixed(2)}x
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 🟢 BOTTOM CONSOLE: CONTROLS (FIXED TO BOTTOM ON MOBILE) */}
                    <div className="fixed bottom-0 left-0 w-full z-[100] lg:static lg:z-50 lg:mt-auto bg-black border-t-2 border-zinc-800/50 flex flex-row items-center p-2 lg:p-0 lg:px-8 lg:py-4 gap-2 lg:gap-4 shrink-0 rounded-none shadow-[0_-10px_30px_rgba(0,0,0,0.8)] lg:shadow-none">
                        
                        {/* Main Button Action */}
                        <div className="w-full flex-1 h-14 lg:h-20 flex rounded-none order-1">
                            {gameState === 'idle' ? (
                                <button onClick={startLobby} className="w-full h-full rounded-none bg-gradient-to-r from-cyan-950/20 to-black border border-cyan-800/30 text-cyan-400 font-black uppercase tracking-[0.2em] text-sm lg:text-2xl hover:from-cyan-900/40 transition-all shadow-[0_0_20px_rgba(34,211,238,0.1)]">
                                    Start Engine
                                </button>
                            ) : gameState === 'waiting' ? (
                                localBalance < ENTRY_FEE ? (
                                    <button onClick={onBuyCredits} className="w-full h-full rounded-none bg-gradient-to-r from-purple-950/20 to-black border border-purple-800/30 text-purple-400 font-black uppercase tracking-[0.1em] lg:tracking-[0.2em] text-xs lg:text-lg hover:from-purple-900/40 transition-all shadow-[0_0_20px_rgba(168,85,247,0.1)] flex items-center justify-center gap-1 lg:gap-2">
                                        <Plus size={18}/> Top Up
                                    </button>
                                ) : playerState === 'bet_placed' ? (
                                    <button disabled className="w-full h-full rounded-none bg-gradient-to-r from-red-950/20 to-black text-red-500 border border-red-800/30 font-black uppercase tracking-[0.1em] text-xs lg:text-xl cursor-not-allowed flex flex-col items-center justify-center">
                                        <span>Awaiting Launch</span>
                                    </button>
                                ) : (
                                    <button onClick={placeBet} className="w-full h-full rounded-none bg-gradient-to-r from-cyan-950/30 to-black text-cyan-400 border border-cyan-800/40 font-black uppercase tracking-[0.2em] text-lg lg:text-3xl hover:from-cyan-900/50 transition-all shadow-[0_0_30px_rgba(34,211,238,0.2)] animate-pulse">
                                        BET NOW
                                    </button>
                                )
                            ) : gameState === 'flying' ? (
                                playerState === 'bet_placed' ? (
                                    <button onClick={cashOut} className="w-full h-full rounded-none bg-gradient-to-r from-amber-950/30 to-black text-amber-400 border border-amber-800/40 font-black uppercase tracking-[0.1em] lg:tracking-[0.2em] text-sm lg:text-3xl hover:from-amber-900/50 transition-all shadow-[0_0_40px_rgba(251,191,36,0.15)] flex flex-col items-center justify-center leading-tight">
                                        <span className="flex items-center gap-1 lg:gap-3"><ArrowUpCircle className="w-4 h-4 lg:w-7 lg:h-7"/> EJECT</span>
                                        <span className="text-[10px] lg:text-sm font-mono tracking-widest text-amber-200/70 lg:mt-1">
                                            {(ENTRY_FEE * currentMultiplier).toFixed(0)} CRD
                                        </span>
                                    </button>
                                ) : playerState === 'cashed_out' ? (
                                    <button disabled className="w-full h-full rounded-none bg-gradient-to-r from-emerald-950/20 to-black border border-emerald-800/30 text-emerald-500 font-black uppercase tracking-[0.1em] text-[10px] lg:text-xl cursor-not-allowed">
                                        Secured
                                    </button>
                                ) : (
                                    <button disabled className="w-full h-full rounded-none bg-gradient-to-r from-cyan-950/10 to-black border border-cyan-900/20 text-cyan-600/50 font-black uppercase tracking-[0.1em] text-[10px] lg:text-xl cursor-not-allowed">
                                        In Progress
                                    </button>
                                )
                            ) : (
                                <button disabled className="w-full h-full rounded-none bg-gradient-to-r from-zinc-900/20 to-black border border-zinc-800/30 text-zinc-600 font-black uppercase tracking-[0.1em] text-xs lg:text-xl cursor-not-allowed">
                                    Recalibrating
                                </button>
                            )}
                        </div>

                        {/* Bet Input Section */}
                        <div className="flex w-auto lg:w-56 bg-black border border-cyan-900/30 rounded-none p-1 lg:p-3 items-center flex-col lg:items-start shrink-0 order-2">
                            <span className="hidden lg:block text-[10px] text-cyan-500/70 uppercase tracking-widest font-black mb-1">Bet Amount</span>
                            <div className="bg-gradient-to-r from-zinc-950/20 to-black rounded-none flex items-center px-2 py-1 lg:px-3 lg:py-2 border border-zinc-800/30 w-full h-[40px] lg:h-auto">
                                <input type="number" value={ENTRY_FEE} disabled className="w-10 lg:w-full bg-transparent text-white font-mono text-sm lg:text-xl outline-none rounded-none text-right lg:text-left" />
                                <span className="text-amber-400 font-bold ml-1 lg:ml-2 text-[10px] lg:text-base">CRD</span>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};